import { useState, useEffect, useCallback, useContext } from "react";
import UserView from "../components/UserView";
import AdminView from "../components/AdminView";
import UserContext from "../context/UserContext";
import ProductDetails from "../components/ProductDetails";
import ProductSearch from "../components/ProductSearch";
import Pagination from "react-bootstrap/Pagination";

const PRODUCTS_PER_PAGE = 9;

export default function Products() {
  const { user } = useContext(UserContext);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /*
   * Fetch products
   */
  const fetchData = useCallback(() => {
    if (!user) return;

    const fetchUrl = user.isAdmin
      ? `${process.env.REACT_APP_API_BASE_URL}/products/all`
      : `${process.env.REACT_APP_API_BASE_URL}/products/active`;

    fetch(fetchUrl, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`API returned ${res.status}`);
        }

        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setProducts(data);
          setCurrentPage(1);
          setTotalPages(
            Math.max(1, Math.ceil(data.length / PRODUCTS_PER_PAGE)),
          );
        } else {
          console.error("Fetched data is not an array:", data);

          setProducts([]);
          setTotalPages(1);
        }
      })
      .catch((error) => {
        console.error("Error fetching products:", error);

        setProducts([]);
        setTotalPages(1);
      });
  }, [user]);

  /*
   * Initial product fetch
   */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /*
   * Handle search results
   *
   * Reset pagination whenever a new search is performed.
   */
  const handleSearchResults = useCallback((searchResults) => {
    setProducts(searchResults);
    setCurrentPage(1);

    setTotalPages(
      Math.max(1, Math.ceil(searchResults.length / PRODUCTS_PER_PAGE)),
    );
  }, []);

  /*
   * Product selection
   */
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
  };

  /*
   * Close product modal
   */
  const handleCloseModal = () => {
    setSelectedProduct(null);
  };

  /*
   * Pagination
   */
  const indexOfLastProduct = currentPage * PRODUCTS_PER_PAGE;

  const indexOfFirstProduct = indexOfLastProduct - PRODUCTS_PER_PAGE;

  const currentProducts = products.slice(
    indexOfFirstProduct,
    indexOfLastProduct,
  );

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  /*
   * Generate page numbers
   */
  const pageNumbers = [];

  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <>
      {/* Search for regular users */}
      {!user?.isAdmin && (
        <ProductSearch onSearchResults={handleSearchResults} />
      )}

      {/* Product listing */}
      {user?.isAdmin ? (
        <AdminView productsData={currentProducts} fetchData={fetchData} />
      ) : (
        <UserView
          productsData={currentProducts}
          handleProductSelect={handleProductSelect}
        />
      )}

      {/* Product details */}
      {selectedProduct && (
        <ProductDetails product={selectedProduct} onClose={handleCloseModal} />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-wrapper">
          <Pagination>
            <Pagination.First
              disabled={currentPage === 1}
              onClick={() => paginate(1)}
            />

            <Pagination.Prev
              disabled={currentPage === 1}
              onClick={() => paginate(currentPage - 1)}
            />

            {pageNumbers.map((number) => (
              <Pagination.Item
                key={number}
                active={number === currentPage}
                onClick={() => paginate(number)}
              >
                {number}
              </Pagination.Item>
            ))}

            <Pagination.Next
              disabled={currentPage === totalPages}
              onClick={() => paginate(currentPage + 1)}
            />

            <Pagination.Last
              disabled={currentPage === totalPages}
              onClick={() => paginate(totalPages)}
            />
          </Pagination>
        </div>
      )}
    </>
  );
}
